import {GetPercentFromXToY, Lerp, Timer, WaitXThenRun} from "js-vextensions";
import {EngineConfig_Visualization, PromptPool, VisPromptWeightGroup, VisualizationPromptPoolType} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Visualization.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {Capacitor} from "@capacitor/core";
import {EngineSessionComp, StatusUIObj} from "./EngineSessionComp.js";
import {FilterWordList_ForVisualization, GetWordList} from "./Visualization/WordLists.js";
import {GetGridScenesData} from "./Visualization/GridScenes.js";
import {ConvertDigitsToWords, NarrateText_ForEngineComp, NotifyNonTTSUtteranceEnd, NotifyNonTTSUtteranceStart, StandardizeTextForTTS} from "../../../Utils/Services/TTS.js";
import {Log_Direct} from "../../../Utils/General/Logging.js";
import {AlarmsComp, PhaseChange_ExtraMessage} from "./AlarmsComp.js";
import {MicroSnoozeComp} from "./MicroSnoozeComp.js";
import {SessionEvent} from "../SessionEvent.js";
import React from "react";
import {Row} from "react-vcomponents";
import {GetNow_TrackedAndThrottled} from "../../../Store/main/@Special/Timing.js";
import {GetAudioFilesWithSubpath} from "../../../Store/main/general/CacheState.js";
import {PlaySound} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {GetDistancesFromRoomToAllOthers, AddSuffixToPromptPoolTargetTTSText, ParseCustomPromptPool, PromptPoolTargetToTTSText, Room, Target} from "./Visualization/VisualizationUtils.js";
import {MediaComp} from "./MediaComp.js";
import {EffectPointerPlayer} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";
import {GetStandardFilePathForTTSOptions, GetStandardFilePathForTTSOptions_ForVisualization} from "../../../Utils/Services/FilePaths.js";
import {CycleCountType} from "../../../Server/DB/@Shared/EffectPointer.js";
import {minuteInMS} from "web-vcore";
import {PLACEHOLDER_TIMER_INTERVAL} from "../../../Utils/LibIntegrations/MobXGraphlink.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

/** Cache for a single prompt pool's target data */
class PoolState {
	targets_rawData: Room[] = [];
	/** Multiplier-expanded list of prompts for pool. (each entry = one unit of pick-weight) */
	targets_flat: Target[] = [];
	recentPrompts: Array<{target: Target, room: string|n}> = []; // history of recent prompts for blocking repeats

	// if poolType:gridScenes
	/** Cells (letters) used so far in the current "round-robin cycle". Reset once every available cell has been used. */
	roundRobin_cellsUsed = new Set<string>();
	/** Number of prompts played in this pool since the pool's (effective) target-generation last changed. */
	bumpTargetGen_promptsSinceLastTargetGenChange = 0;

	// if poolType:customSounds
	// custom sounds playback state
	cs_effectPlayer?: EffectPointerPlayer;
}

/** Info about the main prompt most recently played, used as the basis for subprompt repetition. */
interface SubpromptsMainPromptInfo {
	target: Target;
	textToRepeat: string; // final text of the last main prompt played
	poolToUse: PromptPool; // pool that was used for the last main prompt
}

export const visComp_firstPromptDelay = 3; // in seconds

export class VisualizationComp extends EngineSessionComp<EngineConfig_Visualization> {
	constructor(session: LiveSession, config: EngineConfig_Visualization) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}

	override GetStatusUI(): StatusUIObj {
		const sessionEvents = this.s.coreData.events;
		// this is just for mobx-tracking/ui-updating // commented; forgot that caller of GetStatusUI() already updates 1/sec
		//const now_throttled = GetNow_TrackedAndThrottled(1000);

		// count visualization prompts that have occurred so far in this session
		let vizPrompts = 0;
		let lastStartEvent: SessionEvent|n;
		const endPromptingPeriod = (endDate: number)=>{
			if (lastStartEvent?.visualization_startPrompting) {
				const promptBlockDuration = endDate - lastStartEvent.date;
				const promptInterval = lastStartEvent.visualization_startPrompting.promptInterval;
				const promptsInThisBlock = Math.floor(promptBlockDuration / promptInterval);
				vizPrompts += promptsInThisBlock;
			}
		};
		for (const event of sessionEvents) {
			if (event.type == "Visualization.StartPrompting") {
				// end the previous prompting period if one exists, then start a new one
				endPromptingPeriod(event.date);
				lastStartEvent = event;
			} else if (event.type == "Visualization.StopPrompting") {
				// end the current prompting period
				endPromptingPeriod(event.date);
				lastStartEvent = undefined;
			}
		}
		// if still prompting, count prompts up to now
		endPromptingPeriod(Date.now());

		return {
			omitWrapper: true,
			ui: <>
				<Row mt={5}>Visualization... prompts: {vizPrompts}</Row>
				{this.recentPrompts.map((p, i)=>{
					const sceneLetter = p.target.sceneInfo?.letter;
					const str = `${-1 - i}: ${p.target.text}${sceneLetter ? ` [${sceneLetter}]` : ""}`;
					return (
						<Row key={i} mt={2} style={{fontSize: 11}}>{str}</Row>
					);
				})}
			</>
		};
	}

	GetTriggerPackages() {
		return [
			new TriggerPackage("Visualization_StartForceEnable", this.c.startForceEnable_triggerSet, this, {}, triggerInfo=>{
				this.StartPromptingForceEnable(this.c.startForceEnable_expireAfterOverride);
			}),
			new TriggerPackage("Visualization_StopForceEnable", this.c.stopForceEnable_triggerSet, this, {}, triggerInfo=>{
				this.StopPromptingForceEnable(this.c.stopForceEnabled_silently);
			}),
			new TriggerPackage("Visualization_ToggleForceEnable", this.c.toggleForceEnable_triggerSet, this, {}, triggerInfo=>{
				this.TogglePromptingForceEnable(this.c.toggleForceEnable_expireAfterOverride);
			}),
			new TriggerPackage("Visualization_NextPromptPool", this.c.nextPromptPool_triggerSet, this, {}, triggerInfo=>{
				this.NextPromptPool();
			}),
			new TriggerPackage("Visualization_IncreaseMaxGridGenDistance", this.c.increaseMaxGridGenDistance_triggerSet, this, {}, triggerInfo=>{
				this.AdjustMaxGridGenDistance(+1, true);
			}),
			new TriggerPackage("Visualization_DecreaseMaxGridGenDistance", this.c.decreaseMaxGridGenDistance_triggerSet, this, {}, triggerInfo=>{
				this.AdjustMaxGridGenDistance(-1, true);
			}),
			new TriggerPackage("Visualization_IncreaseTargetGridGen", this.c.increaseTargetGridGen_triggerSet, this, {}, triggerInfo=>{
				this.AdjustTargetGridGen(+1, true);
			}),
			new TriggerPackage("Visualization_DecreaseTargetGridGen", this.c.decreaseTargetGridGen_triggerSet, this, {}, triggerInfo=>{
				this.AdjustTargetGridGen(-1, true);
			}),
			new TriggerPackage("Visualization_IncreaseInterval", this.c.increaseInterval_triggerSet, this, {}, triggerInfo=>{
				this.IncreaseInterval();
			}),
			new TriggerPackage("Visualization_DecreaseInterval", this.c.decreaseInterval_triggerSet, this, {}, triggerInfo=>{
				this.DecreaseInterval();
			}),
			new TriggerPackage("Visualization_IncreaseRevolutionDurations", this.c.increaseRevolutionDurations_triggerSet, this, {}, triggerInfo=>{
				this.IncreaseRevolutionDurations();
			}),
			new TriggerPackage("Visualization_DecreaseRevolutionDurations", this.c.decreaseRevolutionDurations_triggerSet, this, {}, triggerInfo=>{
				this.DecreaseRevolutionDurations();
			}),
		];
	}

	promptingForceEnabled = false;
	forceEnabled_expireTimer = new Timer(this.c.forceEnabled_expireAfter * minuteInMS, ()=>{
		this.StopPromptingForceEnable(this.c.forceEnabled_expireSilently);
	}, 1).SetContext(this.s.timerContext)
	StartPromptingForceEnable(expireAfterOverride: number|n) {
		// the override is honored for the force-enabled state's whole lifetime (so also when resetting an already-active expire countdown)
		const expireAfter = expireAfterOverride ?? this.c.forceEnabled_expireAfter;
		if (this.promptingForceEnabled) {
			// if already force-enabled, but there's an expire timer active, reset its countdown
			if (expireAfter > 0) {
				this.forceEnabled_expireTimer.Start(expireAfter * minuteInMS);
			}
			return;
		}
		
		this.promptingForceEnabled = true;
		NarrateText_ForEngineComp(this, `Starting forced, at ${this.GetIntervalRangeString()}`, this.CurrentPoolPromptVolume());
		this.UpdatePromptingEnabled();
		// start expire timer, if enabled (expireAfter <= 0 means disabled)
		if (expireAfter > 0) {
			this.forceEnabled_expireTimer.Start(expireAfter * minuteInMS);
		}
	}
	GetIntervalRangeString() {
		const effectiveIntervals = this.c.promptPools.filter(a=>a.enabled).map(p=>this.GetEffectiveIntervalForPool(p));
		const minEffectiveInterval = Math.min(...effectiveIntervals);
		const maxEffectiveInterval = Math.max(...effectiveIntervals);
		if (minEffectiveInterval == maxEffectiveInterval) {
			return `${(minEffectiveInterval / 1000).RoundTo_Str(.1)} seconds`;
		}
		return `${(minEffectiveInterval / 1000).RoundTo_Str(.1)} to ${(maxEffectiveInterval / 1000).RoundTo_Str(.1)} seconds`;
	}
	StopPromptingForceEnable(silently = false) {
		if (!this.promptingForceEnabled) return;
		// cancel any pending expire timer (eg. if force-stop is triggered manually before expiry)
		this.forceEnabled_expireTimer.Stop();
		this.promptingForceEnabled = false;
		if (!silently) NarrateText_ForEngineComp(this, `Stopping forced`, this.CurrentPoolPromptVolume());
		this.UpdatePromptingEnabled();
	}
	TogglePromptingForceEnable(expireAfterOverride: number|n = null) {
		if (this.promptingForceEnabled) {
			this.StopPromptingForceEnable();
		} else {
			this.StartPromptingForceEnable(expireAfterOverride);
		}
	}

	/** Force-switch to the next enabled prompt-pool (respecting lock-in reset), without waiting for the next prompt. */
	NextPromptPool() {
		// clear any lock-in so the next pool-selection is a fresh one
		this.currentPoolLockInRemaining = 0;
		this.SelectPool();
		if (this.currentPoolIndex == null) return; // no pools available

		const pool = this.GetCurrentPool();
		if (pool == null) return;

		// stop the old prompt's subprompt-repeat sequence (and clear its repeat state)
		this.subpromptTimer.Stop();
		this.subprompts_mainPromptInfo = null;

		// start a fresh prompt+subprompt sequence, with the same first-prompt delay used on overall prompting start
		this.nextPromptInterval = visComp_firstPromptDelay * 1000;
		if (this.prompting) {
			this.promptTimer.intervalInMS = this.nextPromptInterval;
			this.promptTimer.Start();
		}

		NarrateText_ForEngineComp(this, `Switching to ${pool.name}`, this.CurrentPoolPromptVolume());
	}

	// Pool selection state
	currentPoolIndex: number|n = null;
	currentPoolLockInRemaining = 0; // how many more prompts to use this pool before allowing a switch
	poolStates: PoolState[] = []; // contains state-data (eg. cached flat-list of targets) for each pool
	/** Live adjustment to the targetGridGen value, applied on top of each pool's own targetGridGen.
	 * (modified by the increaseTargetGridGen/decreaseTargetGridGen triggers) */
	targetGridGen_liveAdjustment = 0;
	/** Live adjustment to the maxGridGenDistance limit, applied on top of each pool's own maxGridGenDistance.
	 * (modified by the increaseMaxGridGenDistance/decreaseMaxGridGenDistance triggers) */
	maxGridGenDistance_liveAdjustment = 0;
	CurrentPoolPromptVolume(fallback_useVolumeOfLoudestEnabledPool = true): number {
		const currentPool = this.GetCurrentPool();
		// if no pool is active atm, return a reasonable "fallback prompt-volume"
		if (currentPool == null) {
			if (fallback_useVolumeOfLoudestEnabledPool) {
				const enabledPools = this.c.promptPools.filter(a=>a.enabled);
				if (enabledPools.length > 0) return this.c.promptPools.map(p=>p.promptVolume).Max();
			}
			return 1;
		}
		return currentPool.promptVolume;
	}

	// regular prompt
	promptTimer!: Timer;
	nextPromptInterval!: number; // in ms; the interval to use for the NEXT prompt (set after playing current prompt)
	UpdateIntervalOfPromptTimer_IfActive() {
		// if prompting is active, restart the timer with new interval (if not active, new-interval will be applied once it's started again)
		if (this.prompting) {
			this.promptTimer.intervalInMS = this.nextPromptInterval;
			this.promptTimer.Start();
		}

		this.RestartSubpromptsTimer();
	}
	prompting = false;
	/** The last X voiced prompts, newest-first. Each entry stores the voiced target text plus any
	 * hidden information associated with it (currently only calculated for gridScenes pools; the hidden
	 * info is the grid-cell/letter of the selected scene). Shown in GetStatusUI. */
	recentPrompts: Array<{target: Target}> = [];
	intervalAdjustment = 0; // in seconds; internal adjustment added to each pool's effective interval (modified by hotkeys)
	revolutionDurationAdjustment = 0; // in seconds; adjustment added to revolution duration of spatial audio pools

	// subprompt
	subpromptTimer!: Timer;
	subprompts_mainPromptInfo: SubpromptsMainPromptInfo|n = null; // info about the last main prompt played, used for subprompt repetition
	subprompts_currentIndex = 0; // 0-based index of the next subprompt to play
	/** Number of subprompts scheduled for the current prompt-period (excluding the main prompt itself).
	 * 0-based, so the last subprompt able to be heard is at index `subprompts_totalCount - 1`.
	 * (index is in subprompt-time; real-world times get cut off by the next main prompt's arrival) */
	subprompts_totalCount = 0;
	/** If true, a target-gen bump is due (from "bumpTargetGenAfterXPrompts" hitting its limit), but is being
	 * held back until the triggering prompt-period's final voicing has finished, so its TTS narration
	 * doesn't overlap/interrupt the prompt's own TTS. */
	bumpTargetGen_pending = false;

	/** Get the effective interval for a pool (pool's override or global interval, plus internal adjustment) */
	GetEffectiveIntervalForPool(pool: PromptPool|n): number {
		if (pool == null) return this.c.promptInterval * 1000; // fallback to global interval if no pool
		const baseInterval = pool.promptInterval_override || this.c.promptInterval;
		const intervalInSeconds = Math.max(1, baseInterval + this.intervalAdjustment); // ensure minimum of 1 second
		return intervalInSeconds * 1000; // convert to ms
	}

	/** Get the currently active prompt pool (or null if none available) */
	GetCurrentPool(): PromptPool|n {
		if (this.currentPoolIndex == null) return null;
		return this.c.promptPools[this.currentPoolIndex] ?? null;
	}

	/** Select a new pool based on weights, respecting lock-in settings */
	SelectPool(): void {
		const enabledPools = this.c.promptPools.map((pool, index)=>({pool, index})).filter(p=>p.pool.enabled);
		if (enabledPools.length == 0) {
			this.currentPoolIndex = null;
			this.currentPoolLockInRemaining = 0;
			return;
		}

		// if we're locked into a pool, decrement the counter and keep using it
		if (this.currentPoolLockInRemaining > 0) {
			this.currentPoolLockInRemaining--;
			return; // keep current pool
		}

		// if forceSwitchAfter is set and we just finished a lock-in, ensure we switch pools
		const currentPool = this.GetCurrentPool();
		const mustSwitchPools = currentPool?.lockIn_forceSwitchAfter && this.currentPoolIndex != null;

		// select a new pool based on weights
		let candidatePools = enabledPools;
		if (mustSwitchPools) {
			// exclude current pool
			candidatePools = enabledPools.filter(p=>p.index !== this.currentPoolIndex);
			if (candidatePools.length == 0) {
				// only one pool enabled; can't switch, so just reuse it
				candidatePools = enabledPools;
			}
		}

		// weighted random selection
		const totalWeight = candidatePools.reduce((sum, p)=>sum + p.pool.weight, 0);
		let randomValue = Math.random() * totalWeight;
		let selectedPoolData = candidatePools[0]; // fallback
		for (const poolData of candidatePools) {
			randomValue -= poolData.pool.weight;
			if (randomValue <= 0) {
				selectedPoolData = poolData;
				break;
			}
		}

		this.currentPoolIndex = selectedPoolData.index;
		const newPool = selectedPoolData.pool;
		// set lock-in duration (random between min and max)
		const lockInDuration = Math.floor(Math.random() * (newPool.lockIn_max - newPool.lockIn_min + 1)) + newPool.lockIn_min;
		this.currentPoolLockInRemaining = lockInDuration - 1; // -1 because we're using it for this prompt
	}
	override OnStart() {
		// Initialize with the global default interval
		this.nextPromptInterval = this.c.promptInterval * 1000;
		this.promptTimer = new Timer(this.nextPromptInterval, ()=>{
			this.PlayPrompt();
		}).SetContext(this.s.timerContext);
		this.subpromptTimer = new Timer(PLACEHOLDER_TIMER_INTERVAL, ()=>{
			this.PlaySubprompt();
		}).SetContext(this.s.timerContext);

		// Initialize pool caches
		this.poolStates = this.c.promptPools.map(()=>new PoolState());
		this.UpdatePoolTargets();
	}

	cache_brysbaertWordList: Awaited<ReturnType<typeof GetWordList>> | undefined;
	/** Update the target pool data for all pools. (called on start or when config changes)
	 * Note: This completely synchronously UNLESS there's an x-matching pool of type "brysbaert". */
	async UpdatePoolTargets(onlyUpdatePoolsMatchingX: (pool: PromptPool)=>boolean = ()=>true) {
		for (let i = 0; i < this.c.promptPools.length; i++) {
			const pool = this.c.promptPools[i];
			const cache = this.poolStates[i];
			if (onlyUpdatePoolsMatchingX(pool)) {
				const subresult = this.UpdatePoolTargetsForPool(pool, cache);
				if (subresult instanceof Promise) {
					await subresult;
				}
			}
		}
	}
	/** For most pool types, this function synchronously completes and returns void/undefined.
	 * For Brysbaert pools, a Promise is returned; and cache.targets_* isn't updated till it resolves. */
	UpdatePoolTargetsForPool(pool: PromptPool, cache: PoolState): Promise<void>|void {
		if (pool.type == VisualizationPromptPoolType.custom) {
			// Custom pools are cheap to parse, just do it now (targets carry no sceneInfo)
			cache.targets_rawData = ParseCustomPromptPool(pool.customPoolStr);
			cache.targets_flat = cache.targets_rawData.flatMap(a=>a.targets_full);
			console.log(`Custom pool "${pool.name}" loaded: ${cache.targets_flat.length} targets`);
		} else if (pool.type == VisualizationPromptPoolType.customSounds) {
			// Custom sounds pools don't need target data (they use audio files instead)
			cache.targets_rawData = [];
			cache.targets_flat = [];
			console.log(`Custom sounds pool "${pool.name}" loaded`);
		} else if (pool.type == VisualizationPromptPoolType.brysbaert) {
			return (async()=>{
				// Load word list once if not already loaded
				if (!this.cache_brysbaertWordList) {
					this.cache_brysbaertWordList = await GetWordList();
					Log_Direct("Got word list (as targets):", this.cache_brysbaertWordList);
				}
				cache.targets_flat = FilterWordList_ForVisualization(this.cache_brysbaertWordList, pool, false).map(text=>({text, sceneInfo: null}));
				cache.targets_rawData = []; // No room data for Brysbaert
				console.log(`Brysbaert pool "${pool.name}" loaded: ${cache.targets_flat.length} targets`);
			})();
		} else if (pool.type == VisualizationPromptPoolType.gridScenes) {
			// Build the flat list of eligible scene-targets, applying the target-centered generation-window filter + recency-boosts.
			// Each entry carries its grid-scene info (letter + anchor-entity name), used for last-subprompt suffix substitution.
			const gridScenesData = GetGridScenesData();
			const targetGridGen = Math.max(0, (pool.targetGridGen ?? 0) + this.targetGridGen_liveAdjustment);
			// factor in the live adjustment on top of the pool's own maxGridGenDistance limit (as a symmetric radius around the target)
			const genRadius = Math.max(0, pool.maxGridGenDistance + this.maxGridGenDistance_liveAdjustment);
			const newTargets: Target[] = [];
			for (const candidate of gridScenesData) {
				const genOffset = candidate.generation - targetGridGen; // signed offset from the target generation

				// apply the generation-window filter (symmetric radius around the target generation)
				if (Math.abs(genOffset) > genRadius) continue;

				// apply pick-weight multiplier (keyed by exact generation-offset; default 1 if no match)
				const multiplier = pool.recencyBoosts?.[genOffset] ?? 1;
				for (let i = 0; i < multiplier; i++) {
					newTargets.push({text: candidate.text, sceneInfo: {letter: candidate.letter, entityName: candidate.entityName}});
				}
			}
			cache.targets_flat = newTargets;
			cache.targets_rawData = []; // No room data for grid scenes
			console.log(`Grid-scenes pool "${pool.name}" loaded: ${cache.targets_flat.length} targets`);
		}
	}

	override OnStop() {
		this.StopCustomSoundsPlayback();
	}

	OnStartPhase(oldPhase: Phase, newPhase: Phase, extraMessage: PhaseChange_ExtraMessage): void {
		this.UpdatePromptingEnabled();
	}
	override Unsuspend() {
		const result = super.Unsuspend();
		this.UpdatePromptingEnabled();
		return result;
	}
	override Suspend() {
		const result = super.Suspend();
		this.UpdatePromptingEnabled();
		return result;
	}

	UpdatePromptingEnabled() {
		if (!this.c.enabled) return; // this func can be called externally (eg. from MicroSnoozeComp), so make sure this comp is actually enabled
		const baseEnabled = (()=>{
			// hidden/inferrable extra condition: base-enabled is false when comp is suspended
			if (this.IsSuspended()) return false;
			// hidden/inferrable extra condition: base-enabled is false during the tiny moment when engine is first starting (when phase = "notStarted")
			if (this.s.Comp(AlarmsComp).PhaseIs(Phase.notStarted)) return false;

			if (this.c.promptingBaseEnabledUnless_microSnoozeActive && this.s.Comp(MicroSnoozeComp).snooze_active) return false;
			if (this.c.promptingBaseEnabledUnless_mediaCompPlaying && this.s.Comp(MediaComp).uiState.isPlaying) return false;
			if (this.c.promptingBaseEnabledUnless_inPhases?.includes(this.s.Comp(AlarmsComp).phase)) return false;
			return true;
		})();
		const newPrompting = this.promptingForceEnabled || baseEnabled;
		if (newPrompting && !this.prompting) {
			this.prompting = true;
			this.nextPromptInterval = visComp_firstPromptDelay * 1000; // first prompt always plays 3s after prompting is enabled
			this.promptTimer.intervalInMS = this.nextPromptInterval;
			this.promptTimer.Start();
			this.OnPromptingStartedOrAdjusted();
			// Start custom sounds playback for all enabled customSounds pools
			this.StartCustomSoundsPlayback();
		} else if (!newPrompting && this.prompting) {
			this.prompting = false;
			this.promptTimer.Stop();
			this.subpromptTimer.Stop();
			this.s.AsLocal!.AddEvent({type: "Visualization.StopPrompting"});
			// Stop custom sounds playback for all pools
			this.StopCustomSoundsPlayback();
		}
	}
	OnPromptingStartedOrAdjusted() {
		// Store the global promptInterval (with internal adjustment) for status tracking
		// Note: User should ensure this represents the effective interval after pool weights are considered
		const effectiveInterval = Math.max(1000, (this.c.promptInterval + this.intervalAdjustment) * 1000);
		this.s.AsLocal!.AddEvent({type: "Visualization.StartPrompting", visualization_startPrompting: {promptInterval: effectiveInterval}});
	}

	IncreaseInterval() {
		// Increase the internal interval adjustment
		this.intervalAdjustment += this.c.promptInterval_step;
		this.OnIntervalAdjustmentChanged();
	}
	DecreaseInterval() {
		// Decrease the internal interval adjustment
		this.intervalAdjustment -= this.c.promptInterval_step;
		this.OnIntervalAdjustmentChanged();
	}
	OnIntervalAdjustmentChanged() {
		// add session-event, informing of interval-adjustment
		this.OnPromptingStartedOrAdjusted();

		// "apply" new adjustment-amount, by recalculating the next-prompt-interval (based on current pool + adjustment amount)
		const currentPool = this.GetCurrentPool();
		if (currentPool) {
			this.nextPromptInterval = this.GetEffectiveIntervalForPool(currentPool);
			this.UpdateIntervalOfPromptTimer_IfActive();
		}

		// narrate the new interval/interval-range
		NarrateText_ForEngineComp(this, this.GetIntervalRangeString(), this.CurrentPoolPromptVolume());
	}

	IncreaseRevolutionDurations() {
		// Increase the revolution duration adjustment (slower rotation)
		this.revolutionDurationAdjustment += 1;
		this.OnRevolutionDurationAdjustmentChanged();
	}
	DecreaseRevolutionDurations() {
		// Decrease the revolution duration adjustment (faster rotation)
		this.revolutionDurationAdjustment -= 1;
		this.OnRevolutionDurationAdjustmentChanged();
	}
	/** Increase/decrease the targetGridGen value for gridScenes pools (by the given delta), then rebuild the scene-pools. */
	AdjustTargetGridGen(delta: number, ttsNotify: boolean) {
		this.targetGridGen_liveAdjustment += delta;
		this.OnTargetGridGenAdjustmentChanged(ttsNotify);
	}
	/** Reset the shared targetGridGen_liveAdjustment back to 0 (via AdjustTargetGridGen, so the grid-scenes pools get
	 * rebuilt + the change gets the regular TTS-voicing), if it isn't already 0. */
	ResetTargetGridGen(ttsNotify: boolean) {
		if (this.targetGridGen_liveAdjustment == 0) return;
		this.AdjustTargetGridGen(-this.targetGridGen_liveAdjustment, ttsNotify);
	}
	/** Increase/decrease the maxGridGenDistance limit for gridScenes pools (by the given delta), then rebuild the scene-pools. */
	AdjustMaxGridGenDistance(delta: number, ttsNotify: boolean) {
		this.maxGridGenDistance_liveAdjustment += delta;
		this.OnMaxGridGenDistanceAdjustmentChanged(ttsNotify);
	}
	OnTargetGridGenAdjustmentChanged(ttsNotify: boolean) {
		// the shared target-generation changed, so the "prompts since last target-gen change" counters no longer
		// reflect the pool's effective target-gen; reset them for all gridScenes pools
		for (let i = 0; i < this.c.promptPools.length; i++) {
			if (this.c.promptPools[i].type == VisualizationPromptPoolType.gridScenes) {
				this.poolStates[i].bumpTargetGen_promptsSinceLastTargetGenChange = 0;
			}
		}

		// rebuild the gridScenes pools so the new target-generation takes effect
		this.UpdatePoolTargets(a=>a.type == "gridScenes"); // synchronous

		// narrate the new effective target-generation (across enabled gridScenes pools, after applying the live adjustment)
		if (!ttsNotify) return;
		const gridScenesPools = this.c.promptPools.filter(p=>p.enabled && p.type === VisualizationPromptPoolType.gridScenes);
		if (gridScenesPools.length > 0) {
			const targets = gridScenesPools.map(p=>Math.max(0, (p.targetGridGen ?? 0) + this.targetGridGen_liveAdjustment));
			const minTarget = Math.min(...targets);
			const maxTarget = Math.max(...targets);
			const message = minTarget == maxTarget
				? `${minTarget} target-gen`
				: `${minTarget} to ${maxTarget} target-gens`;
			NarrateText_ForEngineComp(this, message, this.CurrentPoolPromptVolume());
		} else {
			NarrateText_ForEngineComp(this, "No grid-scene pools active", this.CurrentPoolPromptVolume());
		}
	}
	OnMaxGridGenDistanceAdjustmentChanged(ttsNotify: boolean) {
		// rebuild the gridScenes pools so the new generation-distance limit takes effect
		this.UpdatePoolTargets(a=>a.type == "gridScenes"); // synchronous

		// narrate the new effective generation-window (the range of generation-distances included, across enabled gridScenes pools)
		if (!ttsNotify) return;
		const gridScenesPools = this.c.promptPools.filter(p=>p.enabled && p.type === VisualizationPromptPoolType.gridScenes);
		if (gridScenesPools.length > 0) {
			const windows = gridScenesPools.map(p=>{
				const targetGridGen = Math.max(0, (p.targetGridGen ?? 0) + this.targetGridGen_liveAdjustment);
				const radius = Math.max(0, p.maxGridGenDistance + this.maxGridGenDistance_liveAdjustment);
				return {min: Math.max(0, targetGridGen - radius), max: targetGridGen + radius};
			});
			const minGen = Math.min(...windows.map(w=>w.min));
			const maxGen = Math.max(...windows.map(w=>w.max));
			const message = minGen == maxGen
				? `${minGen} generation-distances`
				: `${minGen} to ${maxGen} generation-distances`;
			NarrateText_ForEngineComp(this, message, this.CurrentPoolPromptVolume());
		} else {
			NarrateText_ForEngineComp(this, "No grid-scene pools active", this.CurrentPoolPromptVolume());
		}
	}
	OnRevolutionDurationAdjustmentChanged() {
		// Find active spatial pools and narrate their new revolution durations
		const activeSpatialPools = this.c.promptPools.filter(p=>p.enabled && p.type === VisualizationPromptPoolType.customSounds);
		if (activeSpatialPools.length > 0) {
			const durations = activeSpatialPools.map(p=>{
				const baseDuration = p.cs_effect?.soundFile?.spatial_revolutionDuration ?? 10;
				const effectiveDuration = Math.max(0.1, baseDuration + this.revolutionDurationAdjustment);
				return effectiveDuration.RoundTo_Str(.1);
			});
			const durationsStr = durations.join(", ");
			const message = `${durationsStr} second revolutions`;
			NarrateText_ForEngineComp(this, message, this.CurrentPoolPromptVolume());
		} else {
			// Fallback if no active spatial pools
			const adjust_abs = Math.abs(this.revolutionDurationAdjustment);
			const adjustmentStr = `${this.revolutionDurationAdjustment >= 0 ? "Plus" : "Minus"} ${adjust_abs} second${adjust_abs == 1 ? "" : "s"}`;
			NarrateText_ForEngineComp(this, adjustmentStr, this.CurrentPoolPromptVolume());
		}
	}

	GetFilePathForPrompt(textForTTS: string, pool: PromptPool) {
		if (pool.type == VisualizationPromptPoolType.custom) {
			return GetStandardFilePathForTTSOptions_ForVisualization({
				text: textForTTS,
				ttsVoiceType: pool.ttsVoiceType!,
				elevenLabsVoiceId: pool.elevenLabsVoiceId,
			});
		}
		// For brysbaert pool, use standard Android TTS path
		return GetStandardFilePathForTTSOptions({text: textForTTS, volumeMultiplier: 1, speedMultiplier: 1});
	}

	PlayPrompt() {
		// Select a pool for this prompt (respects lock-in)
		this.SelectPool();
		if (this.currentPoolIndex == null) return; // no pools available
		const pool = this.GetCurrentPool();
		if (pool == null) return;

		// Get cached target pool data (already populated by UpdatePoolData)
		const state = this.poolStates[this.currentPoolIndex];
		if (state.targets_flat.length == 0) return;

		// select a next prompt (looping till we find one different from last time, or till loop-limit hit)
		let newTarget: Target|n;
		let newTargetRoom: string|n;

		// if using custom pool with room data and distance constraints, filter by distance
		if (pool.type == VisualizationPromptPoolType.custom && state.targets_rawData.length > 0) {
			// get distances from current room to all other rooms (in a single graph traversal)
			const distancesMap = GetDistancesFromRoomToAllOthers(state.targets_rawData, state.recentPrompts.LastOrX()?.room);

			// build initial candidate list with distances
			let candidateTargets = [] as Array<{target: Target, room: string, distance: number, jumps: number}>;
			for (const room of state.targets_rawData) {
				const distanceInfo = distancesMap.get(room.name);
				if (distanceInfo == null) continue; // skip if no path found (shouldn't happen in well-formed data)

				// add all targets from this room as candidates
				for (const target of room.targets_full) {
					candidateTargets.push({target, room: room.name, distance: distanceInfo.distance, jumps: distanceInfo.jumps});
				}
			}

			// apply max distance filter (but always include direct neighbors, which have jumps = 1)
			candidateTargets = candidateTargets.filter(c=>c.jumps == 1 || c.distance <= pool.distToNextTarget_max!);

			// apply min distance filter (unless it would exclude all candidates)
			const candidatesAfterMinFilter = candidateTargets.filter(c=>c.distance >= pool.distToNextTarget_min!);
			if (candidatesAfterMinFilter.length > 0) {
				candidateTargets = candidatesAfterMinFilter;
			}

			// apply blockTargetRepeatsForXPrompts filter (unless it would exclude all candidates)
			if (pool.blockTargetRepeatsForXPrompts! > 0) {
				const recentTargets = new Set(state.recentPrompts.slice(-pool.blockTargetRepeatsForXPrompts!).map(p=>p.target.text));
				const candidatesAfterTargetFilter = candidateTargets.filter(c=>!recentTargets.has(c.target.text));
				if (candidatesAfterTargetFilter.length > 0) {
					candidateTargets = candidatesAfterTargetFilter;
				}
			}

			// apply blockRoomRepeatsForXPrompts filter (unless it would exclude all candidates)
			if (pool.blockRoomRepeatsForXPrompts! > 0) {
				const recentRooms = new Set(state.recentPrompts.slice(-pool.blockRoomRepeatsForXPrompts!).map(p=>p.room).filter(r=>r != null));
				const candidatesAfterRoomFilter = candidateTargets.filter(c=>!recentRooms.has(c.room));
				if (candidatesAfterRoomFilter.length > 0) {
					candidateTargets = candidatesAfterRoomFilter;
				}
			}

			// apply equalizeSelectionWeightAmong logic
			if (candidateTargets.length > 0) {
				if (pool.equalizeSelectionWeightAmong == VisPromptWeightGroup.room) {
					// equalize selection weight among rooms: first pick a random room, then a random target from that room
					const candidateRooms = candidateTargets.map(a=>a.room).Distinct();
					const selectedRoom = candidateRooms.Random();
					const targetsInRoom = candidateTargets.filter(c=>c.room == selectedRoom);
					const candidate = targetsInRoom.Random();
					newTarget = candidate.target;
					newTargetRoom = candidate.room;
				} else {
					// equalizeSelectionWeightAmong == VisPromptWeightGroup.target (or default)
					// equalize selection weight among individual targets: just pick a random target directly
					const candidate = candidateTargets.Random();
					newTarget = candidate.target;
					newTargetRoom = candidate.room;
				}
			} else {
				// no candidates available; skip this prompt
				return;
			}
		} else if (pool.type == VisualizationPromptPoolType.gridScenes && state.targets_flat.length > 0) {
			// pick a candidate using the grid-scene selection rules (round-robin across cells if enabled, else random)
			newTarget = this.SelectGridSceneCandidate(state, pool);
		} else {
			// original behavior for non-custom pools or when not using distance constraints
			for (let i = 0; i < 10; i++) {
				newTarget = state.targets_flat.Random();
				if (newTarget.text != state.recentPrompts.LastOrX()?.target.text) break;
			}
		}

		if (newTarget == null) return;

		// record this as one of the last two voiced prompts (newest-first)
		this.recentPrompts.unshift({target: newTarget});
		if (this.recentPrompts.length > 3) {
			this.recentPrompts = this.recentPrompts.slice(0, 3);
		}

		// add to recent prompts history
		state.recentPrompts.push({target: newTarget, room: newTargetRoom});
		// keep recentPrompts list from growing indefinitely (cap at max of the two block settings, plus small buffer)
		const maxHistorySize = Math.max(pool.blockTargetRepeatsForXPrompts!, pool.blockRoomRepeatsForXPrompts!).NaNTo(0) + 3;
		if (state.recentPrompts.length > maxHistorySize) {
			state.recentPrompts = state.recentPrompts.slice(-maxHistorySize);
		}

		// set the next prompt interval based on this pool's interval (plus global adjustment amount)
		this.nextPromptInterval = this.GetEffectiveIntervalForPool(pool);
		this.UpdateIntervalOfPromptTimer_IfActive();

		// if "bumpTargetGenAfterXPrompts" is configured, count this prompt towards the next target-gen bump;
		// if this prompt is the one that hits the limit, mark a bump as "pending" (it gets applied once this
		// prompt-period's final voicing is done -- see BumpTargetGenIfBumpIsPending's callers)
		const bumpAfterXPrompts = pool.bumpTargetGenAfterXPrompts ?? -1;
		if (bumpAfterXPrompts >= 0) {
			state.bumpTargetGen_promptsSinceLastTargetGenChange++;
			if (state.bumpTargetGen_promptsSinceLastTargetGenChange >= bumpAfterXPrompts) {
				this.bumpTargetGen_pending = true;
			}
		}

		// play regular prompt
		const textForTTS = PromptPoolTargetToTTSText(newTarget.text, pool);
		const voicingPromise = this.PlayGivenPromptOrSubprompt(textForTTS, pool, pool.promptVolume);
		this.StartSubpromptsTimer(newTarget, textForTTS, pool);
		// if this period has no subprompts, its main prompt IS the period's final voicing
		if (this.subprompts_totalCount == 0) {
			voicingPromise.then(()=>this.BumpTargetGenIfBumpIsPending());
		}
	}

	/** Apply a pending target-gen bump (from "bumpTargetGenAfterXPrompts" hitting its limit), if there is one.
	 * Called once a prompt-period's final voicing (its last subprompt, or its main prompt if subprompt-less) has
	 * finished, so the bump's TTS narration comes through *after* the prompt's own TTS rather than overlapping it. */
	BumpTargetGenIfBumpIsPending() {
		if (!this.bumpTargetGen_pending) return;
		this.bumpTargetGen_pending = false;
		this.AdjustTargetGridGen(+1, true);
	}

	/** Pick a grid-scene candidate from the given pool's cached flat target list. If avoidRepeatsTillAllCellsUsed is
	 * enabled, selection is "round-robin" across grid-cells: a cell already used this cycle is excluded until
	 * every available cell has been used (at which point the cycle resets). Otherwise, picks uniformly at random. */
	SelectGridSceneCandidate(state: PoolState, pool: PromptPool): Target|n {
		if (state.targets_flat.length == 0) return null;

		// round-robin selection across cells
		// todo: update this branch to still respect the recencyBoosts values
		if (pool.avoidRepeatsTillAllCellsUsed) {
			const unusedCells = ()=>{
				const targetsWithCells = state.targets_flat.filter(t=>t.sceneInfo != null);
				const allCells = targetsWithCells.map(t=>t.sceneInfo!.letter).Distinct();
				return allCells.filter(cell=>!state.roundRobin_cellsUsed.has(cell));
			}

			// if every cell in this pool has been used this cycle, reset the cells-used set
			// (note: the target-gen bump, if the "bumpTargetGenAfterXPrompts" setting is configured, is handled
			// separately by the prompt-count-based logic in PlayPrompt)
			if (unusedCells().length == 0) {
				state.roundRobin_cellsUsed.clear();
			}

			// null if the pool is empty (eg. the gen-window moved past any available scenes)
			const selectedCell = unusedCells().Random();
			if (selectedCell == null) return null;
			state.roundRobin_cellsUsed.add(selectedCell);
			return state.targets_flat.filter(t=>t.sceneInfo?.letter == selectedCell).Random();
		}

		// uniform random selection (weight-multipliers already baked into targets_flat)
		return state.targets_flat.Random();
	}

	StartSubpromptsTimer(target: Target, textToRepeat: string, poolToUse: PromptPool) {
		// track last prompt info for subprompt repetition
		this.subprompts_mainPromptInfo = {target, textToRepeat, poolToUse};
		this.RestartSubpromptsTimer();
	}
	RestartSubpromptsTimer() {
		// compute the subprompt-count for the current prompt-period; always set it (even when 0), since callers
		// use it to know whether the main prompt is the period's final voicing
		const subpromptIntervalS = this.subprompts_mainPromptInfo?.poolToUse.subpromptInterval ?? 0;
		let subpromptCount = 0;
		if (subpromptIntervalS > 0) {
			const promptIntervalS = this.nextPromptInterval / 1000;
			// guard float error: when prompt-interval is an exact multiple of subprompt-interval, ratio can land a hair above
			// the integer, overshooting subpromptCount by 1 and placing the suffix-bearing "last subprompt" at the next main prompt (where it gets cancelled)
			const ratio = promptIntervalS / subpromptIntervalS;
			// upscale -> round -> downscale back; downscale has no error reintroduction (since numerator's a perfect multiple of denominator)
			const ratioSnapped = Math.round(ratio * 1e9) / 1e9;
			subpromptCount = Math.max(0, Math.ceil(ratioSnapped) - 1);
		}
		this.subprompts_totalCount = subpromptCount;
		if (subpromptCount > 0) {
			this.subpromptTimer.Stop();
			this.subpromptTimer.intervalInMS = subpromptIntervalS * 1000;
			this.subpromptTimer.maxCallCount = subpromptCount;
			this.subprompts_currentIndex = 0;
			this.subpromptTimer.Start();
		}
	}
	PlaySubprompt() {
		const mainPromptInfo = this.subprompts_mainPromptInfo;
		if (mainPromptInfo == null) return;
		const {target, textToRepeat, poolToUse: pool} = mainPromptInfo;
		const volumes = pool.subpromptVolumes?.length ? pool.subpromptVolumes : [pool.promptVolume];
		const volume = volumes[Math.min(this.subprompts_currentIndex, volumes.length - 1)];
		// if this is the last subprompt able to be heard (in subprompt-time), apply the last-subprompt TTS suffix
		let textToPlay = textToRepeat;
		const isLastSubprompt = this.subprompts_totalCount > 0 && this.subprompts_currentIndex == this.subprompts_totalCount - 1;
		if (isLastSubprompt) {
			textToPlay = AddSuffixToPromptPoolTargetTTSText(textToPlay, pool, target.sceneInfo);
		}
		this.subprompts_currentIndex++;
		const voicingPromise = this.PlayGivenPromptOrSubprompt(textToPlay, pool, volume);
		// if this is the period's final voicing, apply any pending target-gen bump once it's done
		if (isLastSubprompt) {
			voicingPromise.then(()=>this.BumpTargetGenIfBumpIsPending());
		}
	}

	async PlayGivenPromptOrSubprompt(textForTTS: string, pool: PromptPool, volume: number) {
		// convert target to final TTS text (includes prefix and standardization)
		const filePathToBakedTTS = this.GetFilePathForPrompt(textForTTS, pool);
		const audioFileEntry = GetAudioFilesWithSubpath(filePathToBakedTTS)[0];

		if (audioFileEntry) {
			// Use the baked audio file
			const utteranceId = NotifyNonTTSUtteranceStart(textForTTS);
			await PlaySound({entry: audioFileEntry}, {volume});
			NotifyNonTTSUtteranceEnd(utteranceId);
		} else {
			// Fall back to live TTS
			await NarrateText_ForEngineComp(this, textForTTS, volume);
		}
	}

	StartCustomSoundsPlayback() {
		for (let i = 0; i < this.c.promptPools.length; i++) {
			const pool = this.c.promptPools[i];
			if (!pool.enabled || pool.type !== VisualizationPromptPoolType.customSounds) continue;
			const cache = this.poolStates[i];

			if (!pool.cs_effect?.soundFile?.filePath) {
				console.warn(`Custom sounds pool "${pool.name}" has no sound file configured`);
				continue;
			}

			// Stop any existing player for this pool before starting a new one
			cache.cs_effectPlayer?.Stop();
			const effectPlayer = new EffectPointerPlayer();
			effectPlayer.effectPointer = pool.cs_effect;
			effectPlayer.soundFile_spatial_revolutionDurationAdjustmentGetter = ()=>this.revolutionDurationAdjustment;
			effectPlayer.soundFile_spatial_cycleComplete_countOverrideGetter = pool.cs_effect.soundFile.spatial_cycleComplete_countType === CycleCountType.count_down_solveTimeLeft
				? ()=>{
					const solveTimeRemainingMS = this.s.Comp(AlarmsComp)?.GetSolveTimeCurrentlyRemaining();
					if (solveTimeRemainingMS != null && solveTimeRemainingMS >= 0) {
						return (solveTimeRemainingMS / ((pool.cs_effect!.soundFile!.spatial_revolutionDuration ?? 10) * 1000)).RoundTo(1);
					}
					return null;
				}
				: undefined;
			effectPlayer.soundFile_spatial_onCycleComplete = count=>{
				const soundFile = pool.cs_effect?.soundFile;
				const inCompatiblePhase = !soundFile?.spatial_cycleComplete_countTTSInSolvingPhaseOnly || this.s.Comp(AlarmsComp).PhaseIs(Phase.solving);
				if (inCompatiblePhase) {
					NarrateText_ForEngineComp(this, count.toString(), soundFile?.spatial_cycleComplete_countTTSVolume ?? .1);
				}
			};
			cache.cs_effectPlayer = effectPlayer;
			effectPlayer.Play(pool.promptVolume).catch(e=>console.error(`Failed to start spatial audio for pool "${pool.name}":`, e));
		}
	}
	StopCustomSoundsPlayback() {
		for (const cache of this.poolStates) {
			cache.cs_effectPlayer?.Stop();
			cache.cs_effectPlayer = undefined;
		}
	}

	// "reset target-gen after x minutes of dreaming" logic
	// ==========

	/** The resetTargetGenAfterXMinsOfDreaming value to honor right now, across the currently-enabled grid-scenes pools.
	 * = the minimum value among enabled grid-scenes pools that set it (>= 0); -1 if no enabled grid-scenes pool sets it. */
	GetEffectiveResetTargetGenAfterXMins(): number {
		const mins = this.c.promptPools
			.filter(p=>p.enabled && p.type === VisualizationPromptPoolType.gridScenes)
			.map(p=>p.resetTargetGenAfterXMinsOfDreaming ?? -1)
			.filter(v=>v >= 0); // exclude "disabled" (-1)
		if (mins.length == 0) return -1;
		return Math.min(...mins);
	}
	resetTargetGenAfterXMinsDreaming_timer = new Timer(this.GetEffectiveResetTargetGenAfterXMins() * minuteInMS, ()=>{
		this.ResetTargetGridGen(false);
	}, 1).SetContext(this.s.timerContext);
	
	override OnStartPhase_Dreaming(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage): void {
		const resetAfterXMins = this.GetEffectiveResetTargetGenAfterXMins();
		if (resetAfterXMins < 0) return; // disabled
		if (resetAfterXMins == 0) {
			this.ResetTargetGridGen(false); // reset immediately upon entering dreaming
		} else {
			// arm a timer to fire if the phase stays in dreaming for X straight minutes
			this.resetTargetGenAfterXMinsDreaming_timer.Start();
		}
	}
	override OnLeavePhase_Dreaming(newPhase: Phase, extraMessage: PhaseChange_ExtraMessage): void {
		this.resetTargetGenAfterXMinsDreaming_timer.Stop();
	}
}